// [Adobe]

import { DateStamp, IFormattable, IAnalyticsPushable } from "./analytics.types";

const LAUNCH_SCRIPT_PATH = "https://assets.adobedtm.com/6d67b656c240/5ee6498fb93e";
export const LAUNCH_SCRIPT_URL_PROD = `${LAUNCH_SCRIPT_PATH}/launch-1d21ac8c430f.min.js`;
export const LAUNCH_SCRIPT_URL_UAT = `${LAUNCH_SCRIPT_PATH}/launch-8fa67916b053-staging.min.js`;

// We could maybe create a func that takes an 'env' param, and returns correct script,
// like...
// getLaunchScript(env: 'production' | 'preview' | 'localhost'): string, or similar

/**
 * Global setup
 */

declare global {
  interface Window {
    digitalData: [] | undefined
  }
}

function initGlobalDataLayer(): any {
  if (typeof window === "undefined") {
    throw new Error("window object is undefined, initAnalytics must be called on the client.");
  }
  if (!window.digitalData) {
    window.digitalData = [];
  }
  return window.digitalData;
}

/**
 * Utils
 */

function isDateStamp(value: any): value is DateStamp {
  return typeof value?.now === "function";
}

function resolveData(dataModule: IAnalyticsPushable): Object {
  const formatting = dataModule?.__formatting__;
  if (formatting) {
    delete dataModule.__formatting__;
  }

  for (const [key, value] of Object.entries(dataModule)) {

    // Make a recursive call if the prop is an object
    if (typeof value === "object") {
      dataModule[key] = resolveData(value);
    }

    // Apply date stamps
    if (isDateStamp(value)) {
      dataModule[key] = value.now();
    }

    // Apply any string formatting
    if (formatting?.props.includes(key)) {
      dataModule[key] = formatting.exclude ? value : DashFormatter.format(value)
    }
  }

  return dataModule;
}

function pushAnalytics(data: IAnalyticsPushable): void {
  try {
    const dataLayer = initGlobalDataLayer();
    dataLayer?.push(resolveData(data));
  } catch (error) {
    // If this becomes a reusable module, we should probably notify the client of an error,
    // so they can log or re-throw. Host can catch and log, if it uses a front-end logger.
    console.error(error);
  }
};

export const AnalyticsDateStamp: DateStamp = {
  now() {
    const stamp = new Date();
    return stamp.getFullYear() + "" + (stamp.getMonth() + 1) + "" + stamp.getDate();
  }
}

export const DashFormatter: IFormattable = {
  format(value: string): string {
    const trimmed = value.trim();
    return trimmed ? trimmed.toLowerCase().replace(/[ -]+/g, "-") : trimmed;
  }
}

/**
 * Wrap any exposed behaviour in an adobe obj
 */

const analytics = {
  push<T extends IAnalyticsPushable>(data: T) {
    pushAnalytics(data);
  }
}

export default analytics;